// ============================================
// COUPON HELPERS
// ============================================

/**
 * Predefined coupons for the application
 * In production, this would come from an API
 */
export const availableCoupons = [
  {
    code: 'WELCOME10',
    discount: 10,
    type: 'percentage',
    minOrder: 100,
    maxDiscount: 50,
    description: 'Get 10% off on orders above ₹100',
    validTill: '2026-12-31',
    firstTimeOnly: true
  },
  {
    code: 'MILK20',
    discount: 20,
    type: 'percentage',
    minOrder: 200,
    maxDiscount: 100,
    description: 'Get 20% off on orders above ₹200',
    validTill: '2026-12-31',
    firstTimeOnly: false
  },
  {
    code: 'FLAT50',
    discount: 50,
    type: 'fixed',
    minOrder: 300,
    maxDiscount: 50,
    description: 'Get ₹50 off on orders above ₹300',
    validTill: '2026-12-31',
    firstTimeOnly: false
  },
  {
    code: 'SAVE100',
    discount: 100,
    type: 'fixed',
    minOrder: 500,
    maxDiscount: 100,
    description: 'Get ₹100 off on orders above ₹500',
    validTill: '2026-12-31',
    firstTimeOnly: false
  },
  {
    code: 'MEGA25',
    discount: 25,
    type: 'percentage',
    minOrder: 400,
    maxDiscount: 150,
    description: 'Get 25% off on orders above ₹400',
    validTill: '2026-12-31',
    firstTimeOnly: false
  }
];

/**
 * Validate and apply a coupon
 * @param {string} couponCode - The coupon code to validate
 * @param {number} cartTotal - The current cart total
 * @param {boolean} isFirstOrder - Whether this is user's first order
 * @returns {Object} Validation result with discount info
 */
export const validateCoupon = (couponCode, cartTotal, isFirstOrder = false) => {
  if (!couponCode || typeof couponCode !== 'string') {
    return {
      valid: false,
      error: 'Please enter a coupon code',
      discount: 0
    };
  }

  // Normalize coupon code
  const code = couponCode.trim().toUpperCase();

  // Find the coupon
  const coupon = availableCoupons.find(c => c.code === code);

  if (!coupon) {
    return {
      valid: false,
      error: 'The selected coupon code is invalid.',
      discount: 0
    };
  }

  // Check if coupon is expired
  const validTillDate = new Date(coupon.validTill);
  const today = new Date();
  if (today > validTillDate) {
    return {
      valid: false,
      error: 'This coupon has expired',
      discount: 0
    };
  }

  // Check if coupon is for first-time users only
  if (coupon.firstTimeOnly && !isFirstOrder) {
    return {
      valid: false,
      error: 'This coupon is only valid for first-time orders',
      discount: 0
    };
  }

  // Check minimum order value
  if (cartTotal < coupon.minOrder) {
    return {
      valid: false,
      error: `Minimum order value of ₹${coupon.minOrder} required`,
      discount: 0
    };
  }

  // Calculate discount
  let discount = 0;
  if (coupon.type === 'percentage') {
    discount = (cartTotal * coupon.discount) / 100;
    // Apply max discount limit
    discount = Math.min(discount, coupon.maxDiscount);
  } else if (coupon.type === 'fixed') {
    discount = coupon.discount;
  }

  // Ensure discount doesn't exceed cart total
  discount = Math.min(discount, cartTotal);

  return {
    valid: true,
    discount: Math.round(discount),
    coupon: coupon,
    message: `Coupon applied! You saved ₹${Math.round(discount)}`
  };
};

/**
 * Calculate final amount after applying coupon
 * @param {number} cartTotal - Original cart total
 * @param {number} discount - Discount amount
 * @returns {number} Final amount to pay
 */
export const calculateFinalAmount = (cartTotal, discount = 0) => {
  const finalAmount = cartTotal - discount;
  return Math.max(0, Math.round(finalAmount));
};

/**
 * Get coupon suggestions based on cart total
 * @param {number} cartTotal - Current cart total
 * @returns {Array} Array of applicable coupons
 */
export const getSuggestedCoupons = (cartTotal) => {
  return availableCoupons
    .filter(coupon => {
      // Check if cart total is close to minimum order (within 100 rupees)
      return cartTotal >= coupon.minOrder - 100;
    })
    .sort((a, b) => {
      // Sort by potential savings (highest first)
      const savingsA = a.type === 'percentage' 
        ? Math.min((cartTotal * a.discount) / 100, a.maxDiscount)
        : a.discount;
      const savingsB = b.type === 'percentage'
        ? Math.min((cartTotal * b.discount) / 100, b.maxDiscount)
        : b.discount;
      return savingsB - savingsA;
    })
    .slice(0, 3); // Return top 3 suggestions
};

/**
 * Format coupon discount for display
 * @param {Object} coupon - Coupon object
 * @returns {string} Formatted discount string
 */
export const formatCouponDiscount = (coupon) => {
  if (coupon.type === 'percentage') {
    return `${coupon.discount}% OFF`;
  } else {
    return `₹${coupon.discount} OFF`;
  }
};

/**
 * Remove coupon (reset discount)
 * @returns {Object} Empty coupon state
 */
export const removeCoupon = () => {
  return {
    code: '',
    discount: 0,
    applied: false
  };
};
